import React from 'react';
import { useTranslation } from 'react-i18next';
import { BrandCombobox } from '@/components/BrandCombobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GEAR_TYPES, brandsForType, sizePlaceholder } from '@/data/kiteBrands';
import { Plus, Trash2, Package } from 'lucide-react';

const emptyItem = (type = 'kite') => ({ type, brand: '', size: '', model: '' });

export const GearEditor = ({ value = [], onChange, compact = false }) => {
  const { t } = useTranslation();
  const items = value;

  const update = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  const add = (type = 'kite') => onChange([...items, emptyItem(type)]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3" data-testid="gear-editor">
      {items.length === 0 && (
        <div className="text-center py-6 rounded-xl border border-dashed border-white/12">
          <Package className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t('gearEditor.empty')}</p>
        </div>
      )}

      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-white/10 bg-[#0E1114] p-3.5" data-testid={`gear-item-${i}`}>
          <div className="grid grid-cols-12 gap-2.5 items-end">
            <div className="col-span-6 sm:col-span-3">
              {!compact && <Label className="text-xs text-muted-foreground">{t('gearEditor.type')}</Label>}
              <Select value={it.type} onValueChange={(v) => update(i, { type: v, brand: '' })}>
                <SelectTrigger className="mt-1 bg-[#0A0A0A] border-white/15" data-testid={`gear-type-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#12161B] border-white/15 text-white">
                  {GEAR_TYPES.map((ty) => <SelectItem key={ty.key} value={ty.key}>{t(`myGear.types.${ty.key}`, { defaultValue: ty.label })}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-6 sm:col-span-4">
              {!compact && <Label className="text-xs text-muted-foreground">{t('gearEditor.brand')}</Label>}
              <div className="mt-1">
                <BrandCombobox value={it.brand} onChange={(v) => update(i, { brand: v })}
                  options={brandsForType(it.type)} placeholder={t('gearEditor.brandPlaceholder')} testid={`gear-brand-${i}`} />
              </div>
            </div>
            <div className="col-span-5 sm:col-span-2">
              {!compact && <Label className="text-xs text-muted-foreground">{t('gearEditor.size')}</Label>}
              <Input value={it.size} onChange={(e) => update(i, { size: e.target.value })}
                placeholder={sizePlaceholder(it.type)} className="mt-1 bg-[#0A0A0A] border-white/15" data-testid={`gear-size-${i}`} />
            </div>
            <div className="col-span-5 sm:col-span-2">
              {!compact && <Label className="text-xs text-muted-foreground">{t('gearEditor.model')}</Label>}
              <Input value={it.model} onChange={(e) => update(i, { model: e.target.value })}
                placeholder={t('gearEditor.modelPlaceholder')} className="mt-1 bg-[#0A0A0A] border-white/15" data-testid={`gear-model-${i}`} />
            </div>
            <div className="col-span-2 sm:col-span-1 flex justify-end">
              <button type="button" onClick={() => remove(i)} className="text-muted-foreground hover:text-[#FF3B3B] p-2" data-testid={`gear-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}

      <div className="flex flex-wrap gap-2 pt-1">
        <Button type="button" variant="outline" onClick={() => add('kite')} className="border-[#00B4D8]/40 bg-[#00B4D8]/5 text-[#48CAE4] hover:bg-[#00B4D8]/10 rounded-xl h-9" data-testid="gear-add-kite">
          <Plus className="w-4 h-4 mr-1" /> {t('gearEditor.addKite')}
        </Button>
        <Button type="button" variant="outline" onClick={() => add('board')} className="border-white/15 bg-white/5 hover:bg-white/10 rounded-xl h-9" data-testid="gear-add-board">
          <Plus className="w-4 h-4 mr-1" /> {t('gearEditor.addBoard')}
        </Button>
        <Button type="button" variant="outline" onClick={() => add('harness')} className="border-white/15 bg-white/5 hover:bg-white/10 rounded-xl h-9" data-testid="gear-add-other">
          <Plus className="w-4 h-4 mr-1" /> {t('gearEditor.addOther')}
        </Button>
      </div>
    </div>
  );
};
